import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Text } from '@ltvco/refresh-lib/theme';
import {
  Showroom,
  useUserListings,
  resolveSaveAction,
  buildUserListingBody,
  buildUserListingUpdateBody,
  type UserListing,
  type MarketplaceListing,
} from '@ltvco/refresh-lib/v2';
import { useContext } from 'react';

function openListingInNewTab(listingId: string): void {
  const prefix = (import.meta.env.VITE_PATH_PREFIX as string | undefined) ?? '';
  window.open(
    `${prefix}/marketplace/listing/${listingId}`,
    '_blank',
    'noopener',
  );
}

/**
 * Authenticated showroom route — the user's saved marketplace listings.
 * Ported from the Ember `showroom` route + `vehicles/saved-vehicles` component.
 */
export function MarketplaceShowroom() {
  const {
    listingsQuery,
    createListingMutation,
    updateListingMutation,
    tokenReady,
  } = useUserListings();
  const { trackEvent } = useContext(AppConfig);

  const userListings = listingsQuery.data?.listings ?? [];

  const handleToggleSave = (userListing: UserListing): void => {
    const listing = userListing as MarketplaceListing;
    const plan = resolveSaveAction(userListings, {
      id: userListing.id ?? null,
    });
    // Showroom is a dedicated saved-listings page, not a results-grid card,
    // matching Ember's isCardAction=false case (eventAction: 'Save').
    trackEvent('Save', `Click | ${plan.saved ? 'save' : 'unsave'}`);
    if (plan.kind === 'create') {
      createListingMutation.mutate({
        body: buildUserListingBody(listing),
      });
    } else {
      updateListingMutation.mutate({
        userListingId: plan.userListingId,
        body: buildUserListingUpdateBody(listing, plan.saved),
      });
    }
  };

  const isLoading = !tokenReady || listingsQuery.isLoading;

  const handleListingClick = (listing: UserListing): void => {
    if (listing.id) {
      openListingInNewTab(listing.id);
    }
  };

  return (
    <Box maxWidth={1290} mx="auto" px={2} py={3}>
      <Text variant="h4" fontWeight={600} mb={3}>
        Saved Vehicles
      </Text>
      <Showroom
        userListings={userListings}
        isLoading={isLoading}
        onToggleSave={handleToggleSave}
        onListingClick={handleListingClick}
      />
    </Box>
  );
}
